import { cloneDeep } from 'es-toolkit/compat';
import type { TFunction } from 'i18next';
import { type ConfigObject, DebugLevel, type ModuleCommonProps } from '../../_configs/types';
import { standardFilter } from '../../_flux/defaults';
import { type Facets, type Filter, FilterOrder, FilterSort } from '../../_types/Car';
import { Modules } from '../../modules/_definition/types';
import getFilterFromParamsAndQuery from '../../modules/fahrzeugsuche/filter/utils/getFilterFromParamsAndQuery';
import getSearchFromFilterIdString from '../../utils/texte/getSearchFromFilterIdString';
import getSearchFromString from '../../utils/texte/getSearchFromString';
import checkUndefined from '../../utils/tools/checkUndefined';
import checkUndefinedNullOrEmpty from '../../utils/tools/checkUndefinedNullOrEmpty';

type BuildNewFilterProps = {
    allFacets: Facets;
    initialized: boolean;
    module: Modules;
    params: Readonly<Partial<Record<'hersteller' | 'modell' | 'typ' | 'standort' | 'carDesign' | 'carId', string>>>;
    query: URLSearchParams;
    reset: boolean;
    sessionSearch: Filter;
    settings: ConfigObject;
    translate: TFunction;
};

export default function buildNewFilter(props: BuildNewFilterProps): Filter {
    const { allFacets, initialized, module, params, query, reset, sessionSearch, settings, translate } = props;
    const moduleSettings =
        module === Modules.Aktionsslider ||
            module === Modules.ErweiterteSchnellsuche ||
            module === Modules.Fahrzeugliste ||
            module === Modules.Fahrzeugslider ||
            module === Modules.Fahrzeugsuche ||
            module === Modules.Finanzierungsansicht ||
            module === Modules.Schnellsuche ||
            module === Modules.Standorte
            ? settings?.[module]
            : undefined;

    // Step 1: Initialize new filter with root default filters
    const newFilter: Filter = cloneDeep(standardFilter);
    if (settings?.general?.debug === DebugLevel.FILTER || settings?.general?.debug >= DebugLevel.INFO) {
        console.log(`${module} - initializeCarSearchFilter - standardFilter`, standardFilter);
    }

    // Step 2a: Add default filters from config, but only if no defaultFilterId is set
    if (!initialized && !checkUndefinedNullOrEmpty(moduleSettings?.defaultFilter) && checkUndefinedNullOrEmpty(moduleSettings?.defaultFilterId)) {
        if (settings?.general?.debug === DebugLevel.FILTER || settings?.general?.debug >= DebugLevel.INFO) {
            console.log(`${module} - initializeCarSearchFilter - moduleSettings?.defaultFilter`, moduleSettings?.defaultFilter);
        }
        const defaultFilter = getSearchFromString(moduleSettings?.defaultFilter, allFacets, translate)?.filter;
        (Object.keys(defaultFilter) as (keyof Filter)[])
            ?.filter((key) => !checkUndefined(defaultFilter?.[key]))
            ?.map(
                (key) =>
                    // @ts-expect-error "type any not assignable to type never"
                    (newFilter[key] = defaultFilter?.[key])
            );
    }

    // Step 2b: Add default filter IDs from config, higher priority than default filters
    if (!initialized && !checkUndefinedNullOrEmpty(moduleSettings?.defaultFilterId)) {
        if (settings?.general?.debug === DebugLevel.FILTER || settings?.general?.debug >= DebugLevel.INFO) {
            console.log(`${module} - initializeCarSearchFilter - moduleSettings?.defaultFilterId`, moduleSettings?.defaultFilterId);
        }
        const defaultFilterId = getSearchFromFilterIdString(moduleSettings?.defaultFilterId);
        (Object.keys(defaultFilterId) as (keyof Filter)[])
            ?.filter((key) => !checkUndefined(defaultFilterId?.[key]))
            ?.map(
                (key) =>
                    // @ts-expect-error "type any not assignable to type never"
                    (newFilter[key] = defaultFilterId?.[key])
            );
    }

    // Step 3: Add default sorting from config
    if (!initialized && !checkUndefinedNullOrEmpty(moduleSettings?.defaultSort)) {
        if (settings?.general?.debug === DebugLevel.FILTER || settings?.general?.debug >= DebugLevel.INFO) {
            console.log(`${module} - initializeCarSearchFilter - moduleSettings?.defaultSort`, moduleSettings?.defaultSort);
        }
        (Object.keys(moduleSettings?.defaultSort) as (keyof ModuleCommonProps['defaultSort'])[])
            ?.filter((key) => !checkUndefined(moduleSettings?.defaultSort?.[key]))
            ?.map(
                (key) =>
                    // @ts-expect-error
                    (newFilter[key] = moduleSettings?.defaultSort?.[key])
            );
    }

    // Step 4: Add filter and sorting from session
    if (module === Modules.Fahrzeugsuche && !checkUndefinedNullOrEmpty(sessionSearch)) {
        if (settings?.general?.debug === DebugLevel.FILTER || settings?.general?.debug >= DebugLevel.INFO) {
            console.log(`${module} - initializeCarSearchFilter - sessionSearch`, sessionSearch);
        }
        (Object.keys(sessionSearch) as (keyof Filter)[])
            ?.filter((key) => (!initialized || key === 'sort' || key === 'order') && !checkUndefined(sessionSearch?.[key]))
            ?.map(
                (key) =>
                    // @ts-expect-error
                    (newFilter[key] = sessionSearch?.[key])
            );
    }

    // Step 5: Add filter and sorting from params and query
    const urlFilter = module === Modules.Fahrzeugsuche ? getFilterFromParamsAndQuery(allFacets, settings?.general?.useFirmenFilter, params, query, settings?.general?.useOldRouter) : {};
    if (!checkUndefinedNullOrEmpty(urlFilter)) {
        if (settings?.general?.debug === DebugLevel.FILTER || settings?.general?.debug >= DebugLevel.INFO) {
            console.log(`${module} - initializeCarSearchFilter - urlFilter`, urlFilter);
        }
        (Object.keys(urlFilter) as (keyof Filter)[])
            ?.filter((key) => !checkUndefined(urlFilter?.[key]))
            ?.map(
                (key) =>
                    // @ts-expect-error
                    (newFilter[key] = urlFilter?.[key])
            );
    }

    // Step 6a: Add forced filters from config
    if (!checkUndefinedNullOrEmpty(moduleSettings?.forcedFilter)) {
        if (settings?.general?.debug === DebugLevel.FILTER || settings?.general?.debug >= DebugLevel.INFO) {
            console.log(`${module} - initializeCarSearchFilter - moduleSettings?.forcedFilter`, moduleSettings?.forcedFilter);
        }
        // const moduleSettingsForcedFilterKeys = Object.keys(moduleSettings?.forcedFilter)?.filter((key) => !checkUndefined(moduleSettings?.forcedFilter?.[key]));
        const moduleSettingsForcedFilterKeys = Object.keys(moduleSettings?.forcedFilter)?.filter((key) => !checkUndefined(moduleSettings?.forcedFilter?.[key]));

        if (!checkUndefinedNullOrEmpty(moduleSettingsForcedFilterKeys)) {
            for (let i = 0; i < moduleSettingsForcedFilterKeys?.length; i++) {
                const key = moduleSettingsForcedFilterKeys?.[i];
                newFilter[key] = moduleSettings?.forcedFilter?.[key];
            }
        }
    }

    // Step 6b: Add forced default filters from config
    if (reset === true && !checkUndefinedNullOrEmpty(moduleSettings?.forcedDefaultFilter)) {
        if (settings?.general?.debug === DebugLevel.FILTER || settings?.general?.debug >= DebugLevel.INFO) {
            console.log(`${module} - initializeCarSearchFilter - moduleSettings?.forcedDefaultFilter`, moduleSettings?.forcedDefaultFilter);
        }
        const moduleSettingsForcedFilterKeys = Object.keys(moduleSettings?.forcedDefaultFilter)?.filter((key) => !checkUndefined(moduleSettings?.forcedDefaultFilter?.[key]));
        if (!checkUndefinedNullOrEmpty(moduleSettingsForcedFilterKeys)) {
            for (let i = 0; i < moduleSettingsForcedFilterKeys?.length; i++) {
                const key = moduleSettingsForcedFilterKeys?.[i];
                newFilter[key] = moduleSettings?.forcedDefaultFilter?.[key];
            }
        }
    }

    // Step 7: Fill eventually missing sort and order
    if (checkUndefinedNullOrEmpty(newFilter?.sort)) {
        newFilter.sort = FilterSort.Verkaufspreis;
    }
    if (checkUndefinedNullOrEmpty(newFilter?.order)) {
        newFilter.order = FilterOrder.ASC;
    }

    if (settings?.general?.debug === DebugLevel.FILTER || settings?.general?.debug >= DebugLevel.INFO) {
        console.log(`${module} - initializeCarSearchFilter - newFilter`, newFilter);
    }
    return newFilter;
}
